import { Typography } from "@hellocarbo/lasagne";
import { use, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Outlet,
  useLoaderData,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router";

import { Academy, CommunicationKit } from "@/components/Communicate";
import { UserContext } from "@/contexts";
import { TMedalData, TSimpleReview } from "@/types";

function sortReviews(reviews: Array<TSimpleReview>): Array<TSimpleReview> {
  return reviews
    ?.map((review) => ({
      ...review,
      startDate: new Date(review.period_start),
    }))
    .sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
}

export async function loader() {
  // Fetch all the reviews
  const reviewsResponse = await fetch("/api/client/reviews/");

  if (reviewsResponse.status !== 200) {
    throw new Error("Failed to load the reviews data");
  }

  const { data: reviews } = (await reviewsResponse.json()) as {
    data: Array<TSimpleReview>;
  };

  // Fetch carbo medals
  const carboMedalsResponse = await fetch("/api/client/carbo_medals/");

  if (carboMedalsResponse.status !== 200) {
    throw new Error("Failed to load the medals data");
  }

  const { data: medalsData } = (await carboMedalsResponse.json()) as {
    data: Array<TMedalData>;
  };

  return { reviews: sortReviews(reviews), medalsData };
}

type ContextType = {
  reviews: Array<TSimpleReview>;
  medalsData: TMedalData;
};

// See https://reactrouter.com/en/main/hooks/use-outlet-context
export function useCommunicateContext() {
  return useOutletContext<ContextType>();
}

function Communicate() {
  const { t } = useTranslation("communicate");
  const { user } = use(UserContext);
  const { reviews, medalsData } = useLoaderData<typeof loader>();
  const navigate = useNavigate();
  const { reviewId } = useParams();
  useEffect(() => {
    if (!reviewId) {
      if (user?.company.subscriptionType === "start") {
        navigate(`free-trial`);
      }
      // Redirect to the first review if there is no reviewId param.
      navigate(`${reviews?.[0].id}`);
    }
  }, [user]);

  return (
    <div className="text-typography mx-xl my-lg">
      <Typography step="3" as="h3" weight="bold" className="mb-sm">
        {t("communicate:communication")}
      </Typography>
      <div className="gap-sm lg:gap-md grid grid-cols-1">
        <Outlet context={{ reviews, medalsData }} />
        <Typography step="2" as="h3" weight="bold">
          {t("communicate:ressources")}
        </Typography>
        <div className="gap-sm lg:gap-md grid grid-cols-2">
          <Academy />
          <CommunicationKit />
        </div>
      </div>
    </div>
  );
}

export default Communicate;
